import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
// Force rebuild
import { z } from 'zod';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, useToast, PhoneInput } from '@caruso/ui';
import { clientsService } from '@/services/clients.service';
import { companiesService } from '@/services/companies.service';
import { tagsService } from '@/services/tags.service';
import { ArrowLeft, Save, Plus, X, Search, Activity, ShoppingCart, ChevronLeft, ChevronRight, Clock, MessageSquare, CheckCheck } from 'lucide-react';
import InputMask from 'react-input-mask';
import { CreateClientDto, UpdateClientDto, Tag } from '@caruso/types';
import { cn } from '@caruso/ui';
import 'react-phone-number-input/style.css';

const clientSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  document: z.string().optional(),
  birthDate: z.string().optional(),
  notes: z.string().optional(),
  // Address fields
  zipCode: z.string().optional(),
  address: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

type ClientFormData = z.infer<typeof clientSchema>;

type TabType = 'dados' | 'fluxos' | 'vendas';

export function ClientFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = !!id;
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('dados');

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [flowsPage, setFlowsPage] = useState(1);
  const flowsPerPage = 5;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
  });

  const watchZipCode = watch('zipCode');
  const watchPhone = watch('phone');

  // Load client data if editing
  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsService.getById(id!),
    enabled: isEdit,
  });

  // Load tags
  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: tagsService.getAll,
  });

  // Load flow executions with pagination
  const { data: flowExecutionsData, isLoading: isLoadingFlows } = useQuery({
    queryKey: ['client-flow-executions', id, flowsPage],
    queryFn: () => clientsService.getFlowExecutions(id!, flowsPage, flowsPerPage),
    enabled: isEdit && activeTab === 'fluxos',
  });

  const flowExecutions = flowExecutionsData?.data || [];
  const flowsPagination = flowExecutionsData?.pagination;

  // Load message read statistics
  const { data: messageStats } = useQuery({
    queryKey: ['client-message-stats', id],
    queryFn: () => clientsService.getMessageReadStats(id!),
    enabled: isEdit && activeTab === 'fluxos',
  });

  // Load orders
  const { data: orders = [], isLoading: isLoadingOrders } = useQuery({
    queryKey: ['client-orders', id],
    queryFn: () => clientsService.getOrders(id!),
    enabled: isEdit && activeTab === 'vendas',
  });

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data: ClientFormData) => {
      const cleanedData = {
        ...data,
        tagIds: selectedTags,
        // Send birthDate as string (ISO format) or undefined
        birthDate: data.birthDate || undefined,
        // Clean up phone, CPF and zip code
        phone: data.phone?.replace(/\D/g, '') || undefined,
        document: data.document?.replace(/\D/g, '') || undefined,
        zipCode: data.zipCode?.replace(/\D/g, '') || undefined,
        // Remove empty email
        email: data.email && data.email.trim() !== '' ? data.email : undefined,
      };

      if (isEdit) {
        return clientsService.update(id, cleanedData as UpdateClientDto);
      }
      return clientsService.create(cleanedData as CreateClientDto);
    },
    onSuccess: () => {
      toast({
        title: isEdit ? 'Cliente atualizado' : 'Cliente criado',
        description: 'Operação realizada com sucesso!',
        variant: 'success',
      });
      navigate('/app/clientes');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar cliente',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: () => tagsService.findOrCreate(tagInput),
    onSuccess: (newTag) => {
      setSelectedTags([...selectedTags, newTag.id]);
      setTagInput('');
      setShowTagDropdown(false);
    },
  });

  // CEP lookup effect
  useEffect(() => {
    const searchCep = async () => {
      if (!watchZipCode) return;

      const cleanCep = watchZipCode.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      setIsLoadingCep(true);
      try {
        const address = await companiesService.searchAddress(cleanCep);

        // Auto-fill address fields
        setValue('address', address.street);
        setValue('neighborhood', address.neighborhood);
        setValue('city', address.city);
        setValue('state', address.state);

        if (address.complement) {
          setValue('complement', address.complement);
        }

        toast({
          title: 'CEP encontrado',
          description: 'Endereço preenchido automaticamente',
          variant: 'success',
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          toast({
            title: 'CEP não encontrado',
            description: 'Verifique o CEP digitado',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erro ao buscar CEP',
            description: 'Tente novamente mais tarde',
            variant: 'destructive',
          });
        }
      } finally {
        setIsLoadingCep(false);
      }
    };

    const timeout = setTimeout(searchCep, 500);
    return () => clearTimeout(timeout);
  }, [watchZipCode, setValue, toast]);

  useEffect(() => {
    if (client) {
      setValue('name', client.name);
      setValue('email', client.email || '');

      // Format phone number to E.164 format (+5511999999999)
      let formattedPhone = client.phone || '';
      if (formattedPhone) {
        // Remove non-digits except +
        const cleaned = formattedPhone.replace(/[^\d+]/g, '');
        // If doesn't start with +, add it
        if (!cleaned.startsWith('+')) {
          // If doesn't start with country code, assume Brazilian
          if (!cleaned.startsWith('55') && cleaned.length === 11) {
            formattedPhone = '+55' + cleaned;
          } else if (cleaned.startsWith('55')) {
            formattedPhone = '+' + cleaned;
          } else {
            formattedPhone = '+' + cleaned;
          }
        } else {
          formattedPhone = cleaned;
        }
      }
      setValue('phone', formattedPhone);

      setValue('document', client.document || '');
      setValue('notes', client.notes || '');
      // Address fields
      setValue('zipCode', client.zipCode || '');
      setValue('address', client.address || '');
      setValue('number', client.number || '');
      setValue('complement', client.complement || '');
      setValue('neighborhood', client.neighborhood || '');
      setValue('city', client.city || '');
      setValue('state', client.state || '');

      if (client.birthDate) {
        setValue('birthDate', new Date(client.birthDate).toISOString().split('T')[0]);
      }
      setSelectedTags(client.tags?.map((ct) => ct.tagId) || []);
    }
  }, [client, setValue]);

  const onSubmit = (data: ClientFormData) => {
    mutation.mutate(data);
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleCreateTag = () => {
    if (tagInput.trim()) {
      createTagMutation.mutate();
    }
  };

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(tagInput.toLowerCase())
  );

  if (isLoadingClient) {
    return <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/app/clientes')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-serif text-brand-gold">
          {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
        </h1>
      </div>

      {/* Tabs - Only show for edit mode */}
      {isEdit && (
        <div className="border-b border-brand-gold/20">
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('dados')}
              className={cn(
                'px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2',
                activeTab === 'dados'
                  ? 'border-brand-gold text-brand-gold'
                  : 'border-transparent text-brand-ivory/60 hover:text-brand-ivory hover:border-brand-gold/40'
              )}
            >
              <Search className="w-4 h-4" />
              Dados do Cliente
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('fluxos')}
              className={cn(
                'px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2',
                activeTab === 'fluxos'
                  ? 'border-brand-gold text-brand-gold'
                  : 'border-transparent text-brand-ivory/60 hover:text-brand-ivory hover:border-brand-gold/40'
              )}
            >
              <Activity className="w-4 h-4" />
              Fluxos Executados
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('vendas')}
              className={cn(
                'px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2',
                activeTab === 'vendas'
                  ? 'border-brand-gold text-brand-gold'
                  : 'border-transparent text-brand-ivory/60 hover:text-brand-ivory hover:border-brand-gold/40'
              )}
            >
              <ShoppingCart className="w-4 h-4" />
              Vendas
            </button>
          </div>
        </div>
      )}

      {/* Tab Content: Dados do Cliente */}
      {activeTab === 'dados' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal Information */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Informações Pessoais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-brand-ivory">
                  Nome *
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-brand-ivory">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
                {errors.email && (
                  <p className="text-xs text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-brand-ivory">
                  Telefone
                </Label>
                <PhoneInput
                  id="phone"
                  value={watchPhone}
                  onChange={(value) => setValue('phone', value || '')}
                  defaultCountry="BR"
                  placeholder="Digite o telefone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="document" className="text-brand-ivory">
                  CPF
                </Label>
                <InputMask
                  mask="999.999.999-99"
                  {...register('document')}
                >
                  {(inputProps: any) => (
                    <Input
                      {...inputProps}
                      id="document"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      placeholder="000.000.000-00"
                    />
                  )}
                </InputMask>
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthDate" className="text-brand-ivory">
                  Data de Nascimento
                </Label>
                <Input
                  id="birthDate"
                  type="date"
                  {...register('birthDate')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Endereço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* CEP */}
            <div>
              <Label htmlFor="zipCode" className="text-brand-ivory">
                CEP
              </Label>
              <div className="relative">
                <InputMask
                  mask="99999-999"
                  {...register('zipCode')}
                >
                  {(inputProps: any) => (
                    <Input
                      {...inputProps}
                      id="zipCode"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-10"
                      placeholder="00000-000"
                    />
                  )}
                </InputMask>
                {isLoadingCep && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Search className="w-4 h-4 text-brand-gold animate-pulse" />
                  </div>
                )}
              </div>
              <p className="text-xs text-brand-ivory/50 mt-1">
                Digite o CEP para preencher o endereço automaticamente
              </p>
            </div>

            {/* Endereço e Número */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="address" className="text-brand-ivory">
                  Endereço
                </Label>
                <Input
                  id="address"
                  {...register('address')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Rua, Avenida..."
                />
              </div>

              <div>
                <Label htmlFor="number" className="text-brand-ivory">
                  Número
                </Label>
                <Input
                  id="number"
                  {...register('number')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="123"
                />
              </div>
            </div>

            {/* Complemento e Bairro */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="complement" className="text-brand-ivory">
                  Complemento
                </Label>
                <Input
                  id="complement"
                  {...register('complement')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Apartamento, Sala..."
                />
              </div>

              <div>
                <Label htmlFor="neighborhood" className="text-brand-ivory">
                  Bairro
                </Label>
                <Input
                  id="neighborhood"
                  {...register('neighborhood')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Bairro"
                />
              </div>
            </div>

            {/* Cidade e Estado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-brand-ivory">
                  Cidade
                </Label>
                <Input
                  id="city"
                  {...register('city')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Cidade"
                />
              </div>

              <div>
                <Label htmlFor="state" className="text-brand-ivory">
                  Estado
                </Label>
                <Input
                  id="state"
                  {...register('state')}
                  maxLength={2}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="UF"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes and Tags */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Informações Adicionais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-brand-ivory">
                Observações
              </Label>
              <textarea
                id="notes"
                {...register('notes')}
                rows={4}
                className="w-full rounded-md border border-brand-gold/20 bg-brand-smoke/50 px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-ivory/30 focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-brand-ink"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label className="text-brand-ivory">Tags</Label>
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value);
                      setShowTagDropdown(true);
                    }}
                    onFocus={() => setShowTagDropdown(true)}
                    placeholder="Buscar ou criar tag..."
                    className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                  {showTagDropdown && tagInput && (
                    <div className="absolute z-10 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-md shadow-lg">
                      {filteredTags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            toggleTag(tag.id);
                            setTagInput('');
                            setShowTagDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-brand-ivory hover:bg-brand-gold/10"
                        >
                          {tag.name}
                        </button>
                      ))}
                      {filteredTags.length === 0 && (
                        <button
                          type="button"
                          onClick={handleCreateTag}
                          className="w-full text-left px-3 py-2 text-sm text-brand-gold hover:bg-brand-gold/10"
                        >
                          <Plus className="inline w-3 h-3 mr-1" />
                          Criar "{tagInput}"
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map((tagId) => {
                    const tag = tags.find((t) => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span
                        key={tagId}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-gold/20 text-brand-gold text-sm"
                      >
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => toggleTag(tagId)}
                          className="hover:text-brand-ivory"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/clientes')}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="premium" disabled={mutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      )}

      {/* Tab Content: Fluxos Executados */}
      {activeTab === 'fluxos' && isEdit && (
        <div className="space-y-4">
          {/* Statistics Card */}
          {messageStats && messageStats.totalMessages > 0 && (
            <Card className="bg-gradient-to-r from-brand-ink/50 to-brand-ink/30 border-brand-gold/20">
              <CardHeader>
                <CardTitle className="text-brand-gold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Estatísticas de Mensagens
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-brand-ink/40 rounded-lg p-4 border border-brand-gold/10">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-brand-gold/60" />
                      <p className="text-brand-ivory/60 text-sm">Total de Mensagens</p>
                    </div>
                    <p className="text-2xl font-bold text-brand-gold">{messageStats.totalMessages}</p>
                  </div>

                  <div className="bg-brand-ink/40 rounded-lg p-4 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCheck className="w-4 h-4 text-green-400" />
                      <p className="text-brand-ivory/60 text-sm">Mensagens Lidas</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{messageStats.readMessages}</p>
                    <p className="text-xs text-brand-ivory/40 mt-1">{messageStats.readPercentage}% de taxa de leitura</p>
                  </div>

                  <div className="bg-brand-ink/40 rounded-lg p-4 border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <p className="text-brand-ivory/60 text-sm">Tempo Médio de Leitura</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-400">{messageStats.averageReadTimeFormatted}</p>
                  </div>

                  <div className="bg-brand-ink/40 rounded-lg p-4 border border-brand-gold/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-brand-gold/60" />
                      <p className="text-brand-ivory/60 text-sm">Engajamento</p>
                    </div>
                    <p className="text-2xl font-bold text-brand-gold">
                      {messageStats.readPercentage > 70 ? 'Alto' : messageStats.readPercentage > 40 ? 'Médio' : 'Baixo'}
                    </p>
                    <p className="text-xs text-brand-ivory/40 mt-1">
                      {messageStats.readPercentage > 70 ? 'Ótima taxa de resposta' : messageStats.readPercentage > 40 ? 'Taxa moderada' : 'Necessita atenção'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Flow Executions Card */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">Fluxos Executados</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingFlows ? (
                <div className="text-center py-12 text-brand-ivory/50">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-brand-gold/50 animate-pulse" />
                  <p className="text-sm">Carregando fluxos...</p>
                </div>
              ) : flowExecutions.length === 0 ? (
                <div className="text-center py-12 text-brand-ivory/50">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-brand-gold/50" />
                  <p className="text-lg font-medium mb-2">Nenhum fluxo executado</p>
                  <p className="text-sm">Este cliente ainda não teve nenhum fluxo de WhatsApp executado</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {flowExecutions.map((execution: any) => (
                    <div
                      key={execution.id}
                      className="border border-brand-gold/20 rounded-lg p-4 bg-brand-ink/30"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-brand-gold font-semibold text-lg">
                            {execution.flow?.name || 'Fluxo sem nome'}
                          </h3>
                          {execution.flow?.description && (
                            <p className="text-brand-ivory/60 text-sm mt-1">
                              {execution.flow.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span
                            className={cn(
                              'px-3 py-1 rounded-full text-xs font-medium',
                              execution.status === 'completed'
                                ? 'bg-green-500/20 text-green-400'
                                : execution.status === 'active'
                                ? 'bg-blue-500/20 text-blue-400'
                                : execution.status === 'paused'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            )}
                          >
                            {execution.status === 'completed'
                              ? 'Concluído'
                              : execution.status === 'active'
                              ? 'Ativo'
                              : execution.status === 'paused'
                              ? 'Pausado'
                              : 'Erro'}
                          </span>
                          <p className="text-brand-ivory/40 text-xs mt-2">
                            {new Date(execution.createdAt).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>

                      {execution.messages && execution.messages.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-brand-ivory/60 text-sm font-medium mb-2">
                            Mensagens ({execution.messages.length}):
                          </p>
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {execution.messages.map((message: any) => (
                              <div
                                key={message.id}
                                className={cn(
                                  'p-3 rounded-lg text-sm',
                                  message.direction === 'outbound'
                                    ? 'bg-brand-gold/10 ml-8'
                                    : 'bg-brand-ivory/10 mr-8'
                                )}
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <span
                                    className={cn(
                                      'text-xs font-medium',
                                      message.direction === 'outbound'
                                        ? 'text-brand-gold'
                                        : 'text-brand-ivory'
                                    )}
                                  >
                                    {message.direction === 'outbound' ? '→ Enviada' : '← Recebida'}
                                  </span>
                                  <span className="text-brand-ivory/40 text-xs">
                                    {new Date(message.sentAt).toLocaleTimeString('pt-BR')}
                                  </span>
                                </div>
                                <p className="text-brand-ivory/80">{message.content}</p>
                                <div className="flex items-center gap-3 mt-2">
                                  {message.delivered && (
                                    <span className="text-green-400/60 text-xs inline-flex items-center gap-1">
                                      <CheckCheck className="w-3 h-3" />
                                      Entregue
                                      {message.deliveredAt && (
                                        <span className="text-brand-ivory/30">
                                          {new Date(message.deliveredAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {message.read && (
                                    <span className="text-blue-400 text-xs inline-flex items-center gap-1">
                                      <CheckCheck className="w-3 h-3 fill-blue-400" />
                                      Lida
                                      {message.readAt && (
                                        <span className="text-brand-ivory/30">
                                          {new Date(message.readAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Pagination Controls */}
                  {flowsPagination && flowsPagination.totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between border-t border-brand-gold/20 pt-4">
                      <p className="text-sm text-brand-ivory/60">
                        Mostrando {((flowsPagination.page - 1) * flowsPagination.limit) + 1} a {Math.min(flowsPagination.page * flowsPagination.limit, flowsPagination.total)} de {flowsPagination.total} fluxos
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFlowsPage(p => Math.max(1, p - 1))}
                          disabled={flowsPage === 1}
                          className="border-brand-gold/20 text-brand-gold hover:bg-brand-gold/10"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from({ length: flowsPagination.totalPages }, (_, i) => i + 1).map(page => (
                            <Button
                              key={page}
                              variant={page === flowsPage ? "premium" : "outline"}
                              size="sm"
                              onClick={() => setFlowsPage(page)}
                              className={cn(
                                "w-8 h-8 p-0",
                                page === flowsPage
                                  ? "bg-brand-gold text-brand-ink"
                                  : "border-brand-gold/20 text-brand-gold hover:bg-brand-gold/10"
                              )}
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFlowsPage(p => Math.min(flowsPagination.totalPages, p + 1))}
                          disabled={flowsPage === flowsPagination.totalPages}
                          className="border-brand-gold/20 text-brand-gold hover:bg-brand-gold/10"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Tab Content: Vendas */}
      {activeTab === 'vendas' && isEdit && (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Vendas e Pedidos</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingOrders ? (
              <div className="text-center py-12 text-brand-ivory/50">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-brand-gold/50 animate-pulse" />
                <p className="text-sm">Carregando vendas...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 text-brand-ivory/50">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-brand-gold/50" />
                <p className="text-lg font-medium mb-2">Nenhuma venda registrada</p>
                <p className="text-sm">Este cliente ainda não realizou nenhuma compra</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order: any) => (
                  <div
                    key={order.id}
                    className="border border-brand-gold/20 rounded-lg p-4 bg-brand-ink/30"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-brand-gold font-semibold text-lg">
                          Pedido #{order.orderNumber || order.id.substring(0, 8)}
                        </h3>
                        <p className="text-brand-ivory/60 text-sm mt-1">
                          {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'itens'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span
                          className={cn(
                            'px-3 py-1 rounded-full text-xs font-medium',
                            order.status === 'completed'
                              ? 'bg-green-500/20 text-green-400'
                              : order.status === 'pending'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : order.status === 'processing'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-red-500/20 text-red-400'
                          )}
                        >
                          {order.status === 'completed'
                            ? 'Concluído'
                            : order.status === 'pending'
                            ? 'Pendente'
                            : order.status === 'processing'
                            ? 'Processando'
                            : order.status === 'cancelled'
                            ? 'Cancelado'
                            : order.status}
                        </span>
                        <p className="text-brand-ivory/40 text-xs mt-2">
                          {new Date(order.createdAt).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>

                    {order.items && order.items.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-brand-ivory/60 text-sm font-medium mb-2">Itens:</p>
                        <div className="space-y-2">
                          {order.items.map((item: any) => (
                            <div
                              key={item.id}
                              className="flex justify-between items-center p-3 rounded-lg bg-brand-ivory/5"
                            >
                              <div className="flex-1">
                                <p className="text-brand-ivory font-medium">
                                  {item.product?.name || item.productName || 'Produto'}
                                </p>
                                {item.product?.sku && (
                                  <p className="text-brand-ivory/40 text-xs mt-1">SKU: {item.product.sku}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-brand-ivory/60 text-sm">
                                  {item.quantity}x{' '}
                                  {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(item.price)}
                                </p>
                                <p className="text-brand-gold font-semibold text-sm mt-1">
                                  {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  }).format(item.quantity * item.price)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="pt-3 mt-3 border-t border-brand-gold/20">
                          <div className="flex justify-between items-center">
                            <span className="text-brand-ivory font-semibold">Total:</span>
                            <span className="text-brand-gold font-bold text-xl">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(order.total || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}